import React, { useState } from 'react';
import { compose, mapProps } from 'recompose';
import PropTypes from 'prop-types';
import InsertDriveIcon from '@material-ui/icons/InsertDriveFile';
import DeleteIcon from '@material-ui/icons/Delete';
import DownloadIcon from '@material-ui/icons/Publish';
import { withTranslation } from 'react-i18next';
import { graphql } from 'react-apollo';
import { saveAs } from 'file-saver';

import { toFormattedLocalDate } from 'utils/dateTime/converter';
import { removeAttachment as removeAttachmentOperation, getAttachmentDownloadToken as getAttachmentDownloadTokenOperation } from 'graphql/operations';
import { showErrorMessage } from 'components/common/Alert/Alert';
import DotLoader from 'components/common/Loader/DotLoader/DotLoader';
import Tooltip from 'components/common/Tooltip/Tooltip';
import { useViewport } from 'utils/getViewport/getViewport';
import './Attachment.scss';

export const Attachment = props => {
  const [isDeleting, setDeleting] = useState(false);
  const [isDownloading, setDownloading] = useState(false);
  const { isMobile } = useViewport();

  const { assessmentId, attachment, questionId, removeAttachment, getAttachmentDownloadToken, t, disabled } = props;

  const handleDownloadError = err => {
    console.error('error downloading file', err);
    setDownloading(false);
    showErrorMessage(t('assessment.attachment.download.errorMsg'));
  };

  const deleteAttachment = () => {
    setDeleting(true);
    const variables = { assessmentId, fieldId: questionId, attachmentId: attachment.id };

    removeAttachment({ variables }).catch(() => {
      showErrorMessage(t('assessment.attachment.deleteError'));
    });
  };

  const startDownload = async () => {
    setDownloading(true);
    try {
      // 1) Get attachment download URL
      const variables = { assessmentId, attachmentId: attachment.id };
      const {
        data: {
          getAttachmentDownloadToken: { url: downloadUrl }
        }
      } = await getAttachmentDownloadToken({ variables });

      // 2) Download the file.
      saveAs(downloadUrl, attachment.filename);

      setDownloading(false);
    } catch (err) {
      handleDownloadError(err);
    }
  };

  const onkeyPressDelete = e => {
    if (e.key === 'Enter') {
      deleteAttachment();
    }
  };

  const onkeyPressDownload = async e => {
    if (e.key === 'Enter') {
      try {
        await startDownload();
      } catch (err) {
        handleDownloadError(err);
      }
    }
  };

  let deleteActions = null;
  if (isDeleting) {
    deleteActions = (
      <span className="deleting-message">
        <DotLoader />
      </span>
    );
  } else if (disabled !== true) {
    deleteActions = <DeleteIcon tabIndex={0} className="icon delete-icon" onClick={deleteAttachment} onKeyPress={onkeyPressDelete} />;
  }

  let downloadActions = null;
  if (isDownloading) {
    downloadActions = (
      <span className="downloading-message">
        <DotLoader />
      </span>
    );
  } else {
    downloadActions = <DownloadIcon tabIndex={0} className="icon download-icon" onClick={startDownload} onKeyPress={onkeyPressDownload} />;
  }

  const attachmentTitle = !isMobile ? (
    attachment.filename
  ) : (
    <>
      {attachment.filename} <br /> {t('common.uploaded')} : {toFormattedLocalDate(attachment.createdTime, 'ddd MMM DD YYYY h:mm:ss a')}
    </>
  );

  return (
    <div className="rsa-file" tabIndex={0} role="tab">
      <Tooltip title={attachmentTitle}>
        <span className={!isMobile ? `file-name` : `mobile-file-name`}>
          <InsertDriveIcon />
          <span className="name">{attachment.filename}</span>
          {downloadActions}
        </span>
      </Tooltip>

      <span className="file-time">
        {!isMobile && (
          <div>
            <span className="time-label">{t('common.uploaded')}</span>
            <span className="time-value">{toFormattedLocalDate(attachment.createdTime, 'ddd MMM DD YYYY h:mm:ss a')}</span>
          </div>
        )}
        {deleteActions}
      </span>
    </div>
  );
};

Attachment.propTypes = {
  // TODO: Fix this...
  // eslint-disable-next-line react/forbid-prop-types
  attachment: PropTypes.object.isRequired,
  questionId: PropTypes.string.isRequired
};

export const AttachmentHOC = compose(
  withTranslation(),
  graphql(removeAttachmentOperation, { name: 'removeAttachment' }),
  graphql(getAttachmentDownloadTokenOperation, { name: 'getAttachmentDownloadToken' }),
  mapProps(props => ({
    t: props.t,
    assessmentId: props.assessmentId,
    removeAttachment: props.removeAttachment,
    getAttachmentDownloadToken: props.getAttachmentDownloadToken,
    attachment: props.attachment,
    questionId: props.questionId,
    disabled: props.disabled
  }))
);

export default AttachmentHOC(Attachment);
